/**
 * Gateway profiles — list and single profile for Phase 4.
 * List uses Supabase + RLS; create/update/delete use Edge Functions.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApp } from '@/contexts/AppContext';
import { supabase } from '@/integrations/supabase/client';
import {
  createGatewayProfile,
  updateGatewayProfile,
  deleteGatewayProfile,
  type CreateGatewayProfileInput,
} from '@/services/gateway';
import type { GatewayProfile } from '@/types/gateway';

export function useGatewayProfiles() {
  const { userProfile, loading: appLoading } = useApp();
  const orgId = userProfile?.org_id ?? null;

  const query = useQuery({
    queryKey: ['gateway-profiles', orgId],
    queryFn: async (): Promise<GatewayProfile[]> => {
      if (!orgId) return [];
      const { data, error } = await supabase
        .from('gateway_profiles')
        .select('*')
        .eq('organization_id', orgId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []).map((row) => ({
        id: row.id,
        organization_id: row.organization_id,
        name: row.name,
        description: row.description,
        poll_interval_ms: row.poll_interval_ms ?? 5000,
        upload_interval_ms: row.upload_interval_ms ?? 30000,
        heartbeat_interval_ms: row.heartbeat_interval_ms ?? 60000,
        command_poll_interval_ms: row.command_poll_interval_ms ?? 10000,
        writes_enabled: row.writes_enabled ?? false,
        ota_channel: (row.ota_channel ?? 'stable') as 'pilot' | 'stable',
        expected_devices: Array.isArray(row.expected_devices) ? row.expected_devices : [],
        default_property_config: row.default_property_config ?? null,
        auto_commission: row.auto_commission ?? false,
        auto_create_property: row.auto_create_property ?? false,
        is_default: row.is_default ?? false,
        created_at: row.created_at,
        updated_at: row.updated_at,
        created_by: row.created_by,
      }));
    },
    enabled: Boolean(orgId) && !appLoading,
    staleTime: 60_000,
  });

  return {
    profiles: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useGatewayProfile(profileId: string | null) {
  const { userProfile, loading: appLoading } = useApp();
  const orgId = userProfile?.org_id ?? null;

  const query = useQuery({
    queryKey: ['gateway-profile', profileId, orgId],
    queryFn: async (): Promise<GatewayProfile | null> => {
      if (!profileId || !orgId) return null;
      const { data, error } = await supabase
        .from('gateway_profiles')
        .select('*')
        .eq('id', profileId)
        .eq('organization_id', orgId)
        .single();
      if (error) {
        if (error.code === 'PGRST116') return null;
        throw new Error(error.message);
      }
      if (!data) return null;
      return {
        id: data.id,
        organization_id: data.organization_id,
        name: data.name,
        description: data.description,
        poll_interval_ms: data.poll_interval_ms ?? 5000,
        upload_interval_ms: data.upload_interval_ms ?? 30000,
        heartbeat_interval_ms: data.heartbeat_interval_ms ?? 60000,
        command_poll_interval_ms: data.command_poll_interval_ms ?? 10000,
        writes_enabled: data.writes_enabled ?? false,
        ota_channel: (data.ota_channel ?? 'stable') as 'pilot' | 'stable',
        expected_devices: Array.isArray(data.expected_devices) ? data.expected_devices : [],
        default_property_config: data.default_property_config ?? null,
        auto_commission: data.auto_commission ?? false,
        auto_create_property: data.auto_create_property ?? false,
        is_default: data.is_default ?? false,
        created_at: data.created_at,
        updated_at: data.updated_at,
        created_by: data.created_by,
      };
    },
    enabled: Boolean(profileId) && Boolean(orgId) && !appLoading,
    staleTime: 60_000,
  });

  return {
    profile: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}

export function useCreateGatewayProfile() {
  const queryClient = useQueryClient();
  const { userProfile } = useApp();
  const orgId = userProfile?.org_id ?? null;

  return useMutation({
    mutationFn: (input: CreateGatewayProfileInput) => createGatewayProfile(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway-profiles', orgId] });
    },
  });
}

export function useUpdateGatewayProfile() {
  const queryClient = useQueryClient();
  const { userProfile } = useApp();
  const orgId = userProfile?.org_id ?? null;

  return useMutation({
    mutationFn: ({ profileId, input }: { profileId: string; input: Partial<CreateGatewayProfileInput> }) =>
      updateGatewayProfile(profileId, input),
    onSuccess: (_, { profileId }) => {
      queryClient.invalidateQueries({ queryKey: ['gateway-profiles', orgId] });
      queryClient.invalidateQueries({ queryKey: ['gateway-profile', profileId] });
    },
  });
}

export function useDeleteGatewayProfile() {
  const queryClient = useQueryClient();
  const { userProfile } = useApp();
  const orgId = userProfile?.org_id ?? null;

  return useMutation({
    mutationFn: (profileId: string) => deleteGatewayProfile(profileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gateway-profiles', orgId] });
    },
  });
}
